import React from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../hooks/useLanguage';
import { common as frCommon } from '../../../../locales/fr/common';
import { common as esCommon } from '../../../../locales/es/common';
import { common as arCommon } from '../../../../locales/ar/common';

const DesktopView = ({ data, columns, actions, expandedRows, toggleRow, formatDate, collapsibleColumns }) => {
  const { lang } = useLanguage();
  const translations = {
    fr: frCommon,
    es: esCommon,
    ar: arCommon
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value[k];
    }
    return value;
  };

  return (
    <table className="min-w-full divide-y divide-gray-200" dir="ltr">
      <thead className="bg-indigo-600 sticky top-0 z-10" >
        <tr>
          <th
            scope="col"
            className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg"
          >
            {columns[0]?.label}
          </th>
          {columns.slice(1).map((column) => (
            <th
              key={column.key}
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider"
            >
              {column.label}
            </th>
          ))}
          <th scope="col" className="w-32 px-6 py-3 text-white rounded-tr-lg">
            <span className="text-xs font-medium uppercase tracking-wider">{t('actions.title')}</span>
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200 border-x border-gray-200">
        {data.items.map((item, index) => (
          <React.Fragment key={item.id || index}>
            <tr className={expandedRows.has(item.id) ? 'bg-gray-50' : ''}>
              {columns.map((column) => (
                <td
                  key={`${item.id}-${column.key}`}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200"
                >
                  {column.render ? column.render(item) : item[column.key]}
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border-b border-gray-200">
                <div className="flex justify-end space-x-2">
                  {actions?.row?.map((action, actionIndex) => (
                    <button
                      key={actionIndex}
                      onClick={() => action.onClick(item)}
                      className={action.className}
                      title={t(`actions.${action.icon}`)}
                    >
                      {action.icon === 'edit' ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      ) : action.icon === 'delete' ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      ) : null}
                    </button>
                  ))}
                  <button
                    onClick={() => toggleRow(item.id)}
                    className="text-gray-400 hover:text-gray-500"
                    title={expandedRows.has(item.id) ? t('actions.collapse') : t('actions.expand')}
                  >
                    <svg
                      className={`h-5 w-5 transform ${expandedRows.has(item.id) ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            {expandedRows.has(item.id) && collapsibleColumns && (
              <tr>
                <td colSpan={columns.length + 1} className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="text-sm text-gray-500">
                    <div className="grid grid-cols-3 gap-4">
                      {collapsibleColumns.map((section) => (
                        <div key={section.key}>
                          <h4 className="font-medium text-gray-900">{section.label}</h4>
                          {section.render(item)}
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

DesktopView.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func
    })
  ).isRequired,
  actions: PropTypes.shape({
    row: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        title: PropTypes.string.isRequired,
        className: PropTypes.string
      })
    )
  }),
  expandedRows: PropTypes.instanceOf(Set).isRequired,
  toggleRow: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  collapsibleColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired
    })
  )
};

export default DesktopView; 